import React, { useState } from "react";
import { Check, X, History } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const confCls = (c) => (c >= 0.9 ? "text-emerald-400" : c >= 0.7 ? "text-slate-400" : "text-yellow-300");

// Per-field confidence, learned-mistake warning and ✓ Correct / ✗ Wrong active-learning buttons.
export function AiFieldFeedback({ it, k, f, onConfirm, onReject, t }) {
    const [open, setOpen] = useState(false);
    const [val, setVal] = useState("");
    const hasVal = f.value !== "" && f.value != null;
    const submit = () => { if (val.trim() !== "") { onReject(it, k, val.trim()); setOpen(false); } };
    return (
        <div className="px-1 flex items-center gap-1 flex-wrap">
            {hasVal && !f.edited && <span data-testid={`ai-confpct-${k}-${it._id}`} className={`text-[10px] font-mono ${confCls(f.conf || 0)}`}>{Math.round((f.conf || 0) * 100)}%</span>}
            {f.fb === "ok" && <span data-testid={`ai-fb-ok-${k}-${it._id}`} className="text-[10px] text-emerald-400">✓</span>}
            {f.fb === "fixed" && <span data-testid={`ai-fb-fixed-${k}-${it._id}`} className="text-[10px] text-blue-300">{t("al_saved")}</span>}
            {hasVal && !f.fb && (
                <>
                    <button data-testid={`ai-ok-${k}-${it._id}`} title={t("al_correct")} onClick={() => onConfirm(it, k)}
                        className="w-4 h-4 flex items-center justify-center rounded text-emerald-400 hover:bg-emerald-500/20 transition-colors"><Check className="w-3 h-3" /></button>
                    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setVal(String(f.learned?.suggest ?? "")); }}>
                        <PopoverTrigger asChild>
                            <button data-testid={`ai-wrong-${k}-${it._id}`} title={t("al_wrong")}
                                className="w-4 h-4 flex items-center justify-center rounded text-rose-400 hover:bg-rose-500/20 transition-colors"><X className="w-3 h-3" /></button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-60 z-[70] p-3 bg-[#111827] border-[#1e293b] text-slate-100">
                            <div className="text-xs text-slate-300 mb-1.5">{t("al_enter_correct")}</div>
                            <div className="text-[11px] text-slate-500 mb-2">AI: <span className="font-mono text-rose-300 line-through">{String(f.orig ?? "")}</span></div>
                            <input data-testid={`ai-wrong-input-${k}-${it._id}`} autoFocus value={val} onChange={(e) => setVal(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
                                className="w-full h-8 px-2 bg-[#0b0f17] border border-[#1e293b] rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                            <button data-testid={`ai-wrong-save-${k}-${it._id}`} onClick={submit}
                                className="mt-2 w-full h-8 rounded bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold transition-colors">{t("al_save_fix")}</button>
                        </PopoverContent>
                    </Popover>
                </>
            )}
            {f.learned && !f.edited && (
                <div data-testid={`ai-learned-${k}-${it._id}`} title={t("al_warn", { n: f.learned.count })}
                    className="w-full mt-0.5 text-[10px] leading-tight text-amber-300 flex items-center gap-1 whitespace-nowrap overflow-hidden">
                    <History className="w-3 h-3 shrink-0" />
                    <span className="font-mono font-bold">{f.learned.count}×</span>
                    {f.learned.reassign_to && <span data-testid={`ai-learned-move-${k}-${it._id}`}>→ {t(`ai_f_${f.learned.reassign_to}`)}</span>}
                    {f.learned.suggest !== "" && (
                        <button data-testid={`ai-use-learned-${k}-${it._id}`} onClick={() => onReject(it, k, f.learned.suggest)}
                            className="underline text-amber-200 hover:text-white truncate">{t("al_use", { v: f.learned.suggest })}</button>
                    )}
                </div>
            )}
        </div>
    );
}
